import { useMemo } from 'react';
import { RoiType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { cloneDeep } from 'lodash-es';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { commonApiOptions } from '../../helpers/api-helpers';
import { useRoiRequestPayload, type UseRoiRequestBaseProps } from '../../hooks/use-roi-request-payload';
import {
  generateNoShowRateLocationWiseDemoData,
  generateNoShowRateTimelineDemoData,
  generateOverviewDemoData,
} from '../demo-data/overview';
import { AROverviewData } from '../types';

interface Props extends UseRoiRequestBaseProps {
  fetchNoShowRate?: boolean;
  forceDemoData?: boolean;
  roiType?: RoiType;
}

type LocationData = {
  appointmentsCompletedByReminders: number;
  noShowRate: number;
  totalAppointmentsCompleted: number;
};

interface UseFetchARROIOOverview {
  aggregated: AROverviewData;
  isLoading?: boolean;
  locationsData: {
    breakup: {
      [locationId: string]: LocationData & { location_name: string };
    };
    noShowRateOfLocationsNotUsingReminders: number;
  };
}

const defaultAggregated: AROverviewData = {
  appointmentsCompleted: {
    appointmentsCompletedByReminders: 0,
    appointmentsCompletedPatients: 0,
    totalAppointmentsCompleted: 0,
  },
  appointmentsConfirmed: {
    appointmentsCount: 0,
    patientsCount: 0,
  },
  noShowRate: {
    difference: 0,
    groups: [],
  },
  remindersSent: {
    patientsCount: 0,
    remindersCount: 0,
  },
  totalAppointments: 0,
  noShowRatePercentage: 0,
};

export const useFetchARROIOOverview = ({
  fetchNoShowRate = true,
  forceDemoData,
  roiType = RoiType.APPOINTMENT_REMINDERS,
  ...rest
}: Props): UseFetchARROIOOverview => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { accessibleLocationData } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = forceDemoData || getFeatureFlagValue(featureFlags.appointmentRemindersRoiDemoData);

  const { request, skipRequest } = useRoiRequestPayload({
    ...rest,
    additionalRequestPayload: {
      roiType,
    },
    isDemoAccount,
  });

  const { data: overviewData, isLoading: isLoadingOverviewData } =
    AnalyticsGatewayV1.Queries.useGetROIAggregatesV2Query({
      request,
      options: {
        ...commonApiOptions,
        enabled: !skipRequest,
        onError: () => {
          alert.error(t("Couldn't fetch the appointment reminders conversion overview details. Please try again."));
        },
      },
    });

  const { data: noShowRateTimeline, isLoading: isLoadingNoShowRateTimeline } =
    AnalyticsGatewayV1.Queries.useGetNoShowRateQuery({
      request,
      options: {
        ...commonApiOptions,
        enabled: !skipRequest && fetchNoShowRate,
        onError: () => {
          alert.error(t("Couldn't fetch the appointment reminders no show rate details. Please try again."));
        },
      },
    });

  const { data: noShowRateLocationWiseData, isLoading: isLoadingNoShowRateLocationWise } =
    AnalyticsGatewayV1.Queries.useGetNoShowRateAggregatesQuery({
      request,
      options: {
        ...commonApiOptions,
        enabled: !skipRequest && fetchNoShowRate,
        onError: () => {
          alert.error(t("Couldn't fetch the appointment reminders no show rate details. Please try again."));
        },
      },
    });

  const processedLocationsNoShowRateData = useMemo(() => {
    const dataToUse = isDemoAccount
      ? generateNoShowRateLocationWiseDemoData(rest.filters?.locationIds ?? [])
      : noShowRateLocationWiseData;

    return {
      processed: dataToUse?.data?.locations?.reduce<Record<string, number>>(
        (acc, { locationId = '', noShowRate = 0 }) => ({ ...acc, [locationId]: noShowRate * 100 }),
        {}
      ),
      rawData: dataToUse?.data,
    };
  }, [isDemoAccount, noShowRateLocationWiseData, rest.filters?.locationIds]);

  const processedOverviewData = useMemo(() => {
    const aggregated: UseFetchARROIOOverview['aggregated'] = cloneDeep(defaultAggregated);
    const dataToUse = isDemoAccount ? generateOverviewDemoData(rest.filters?.locationIds ?? []) : overviewData;

    if (!dataToUse || !Array.isArray(dataToUse.data)) {
      return {
        aggregated,
        locationsData: {
          breakup: {},
          noShowRateOfLocationsNotUsingReminders: 0,
        },
      };
    }

    const locationsData = dataToUse.data?.reduce<UseFetchARROIOOverview['locationsData']>(
      (
        acc,
        {
          appointmentsCompleted = 0,
          appointmentsCompletedPatients = 0,
          appointmentsConfirmed = 0,
          appointmentsConfirmedPatients = 0,
          locationId,
          patients = 0,
          sent = 0,
          totalAppointments = 0,
          totalAppointmentsCompleted = 0,
        }
      ) => {
        aggregated.appointmentsCompleted.appointmentsCompletedByReminders += appointmentsCompleted;
        aggregated.appointmentsCompleted.totalAppointmentsCompleted += totalAppointmentsCompleted;
        aggregated.appointmentsCompleted.appointmentsCompletedPatients += appointmentsCompletedPatients;

        aggregated.appointmentsConfirmed.appointmentsCount += appointmentsConfirmed;
        aggregated.appointmentsConfirmed.patientsCount += appointmentsConfirmedPatients;

        aggregated.remindersSent.patientsCount += patients;
        aggregated.remindersSent.remindersCount += sent;

        aggregated.totalAppointments += totalAppointments;

        if (locationId) {
          acc.breakup[locationId] = {
            appointmentsCompletedByReminders: appointmentsCompleted,
            location_name: accessibleLocationData[locationId]?.name || locationId,
            noShowRate: processedLocationsNoShowRateData?.processed?.[locationId] || 0,
            totalAppointmentsCompleted: totalAppointmentsCompleted,
          };
        }

        return acc;
      },
      {
        breakup: {},
        noShowRateOfLocationsNotUsingReminders: (processedLocationsNoShowRateData?.rawData?.unsubscribed || 0) * 100,
      }
    );

    return {
      aggregated: {
        ...aggregated,
        noShowRatePercentage:
          ((aggregated.totalAppointments - aggregated.appointmentsCompleted.totalAppointmentsCompleted) /
            (aggregated.totalAppointments || 1)) *
          100,
      },
      locationsData,
    };
  }, [
    accessibleLocationData,
    isDemoAccount,
    overviewData,
    processedLocationsNoShowRateData,
    rest.filters?.locationIds,
  ]);

  const processedNoShowRateTimeline = useMemo(() => {
    const noShowRate: UseFetchARROIOOverview['aggregated']['noShowRate'] = {
      difference: 0,
      enableNoShowRateBestPractices: false,
      groups: [],
    };

    const dataToUse = isDemoAccount ? generateNoShowRateTimelineDemoData() : noShowRateTimeline;

    if (!dataToUse) {
      return {
        noShowRate,
      };
    }

    const data = dataToUse.data;

    noShowRate.difference = (data?.rateDifference || 0) * 100;
    noShowRate.groups =
      data?.dates?.map(({ key = '', subscribed = 0, unsubscribed = 0 }) => {
        return {
          name: key,
          values: {
            yourPractice: subscribed * 100,
            noRemindersPractice: unsubscribed * 100,
          },
        };
      }) || [];

    // A positive difference means the practice is doing better than the unsubscribed locations (lower no show rate)
    // Hence show the best practices when the difference is negative
    noShowRate.enableNoShowRateBestPractices = noShowRate.difference <= 0;

    return {
      noShowRate,
    };
  }, [isDemoAccount, noShowRateTimeline]);

  return {
    ...processedOverviewData,
    aggregated: {
      ...processedOverviewData.aggregated,
      ...processedNoShowRateTimeline,
    },
    isLoading: isLoadingOverviewData || isLoadingNoShowRateTimeline || isLoadingNoShowRateLocationWise,
  };
};
